import React, { useReducer, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  CisContainer,
  Select,
  Datepicker,
  Spinner,
} from 'components';
import { isLoading, globalPropTypes } from 'helpers';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  attachIpmaCisInvoiceCertificatesCreator,
  detachIpmaCisInvoiceCertificatesCreator,
  loadClientMemberAssociationsByMACreator,
  loadGlobalCisInvoicesAttributesCreator,
  loadIpmaCisInvoiceCertificatesCreator,
  removeIpmaCisInvoiceCertificatesCreator,
  setIpmaCisEditErrorsCreator,
  updateIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  loadIpmaCisInvoiceInvoicedCertificatesCreator,
  loadIpmaCisInvoiceUninvoicedCertificatesCreator,
  setClientMemberAssociationsCreator,
} from '../../../../../store/actionsCreators';
import moment from 'moment';
import Checkbox from '../../../../../components/Checkbox/Checkbox';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { typeOptions } from 'shared/Cis/CisOptions';

const EditInvoice = ({
  invoice,
  errors,
  updateInvoice,
  removeError,
  ma,
  loadInvoicesAttributes,
  loadCertificates,
  loadInvoicedCertificates,
  loadUninvoicedCertificates,
  removeCertificates,
  attachCertificates,
  detachCertificates,
  invoicedCertificates,
  uninvoicedCertificates,
  certificatesLoading,
  clientMemberAssociations,
  loadClientMemberAssociations,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setClientMemberAssociations,
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  useEffect(() => {
    if (invoice) {
      dispatch(setEditDataCreator(invoice));
    }
  }, [invoice]);

  useEffect(() => {
    loadInvoicesAttributes();
  }, []);

  useEffect(() => {
    if (state.memberAssociation) {
      loadClientMemberAssociations(state.memberAssociation);
    }
    return () => {
      setClientMemberAssociations([]);
    };
  }, [state.memberAssociation]);

  useEffect(() => {
    if (invoice.id) {
      loadCertificates(invoice.id);
    }
    return () => {
      removeCertificates();
    };
  }, [invoice.id]);

  const handleChangeNative = (name, errorName) => (e) => {
    dispatch(updateFieldCreator({ name, value: e.target.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeCustom = (name, errorName) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleSelectRow = (selectedIdsName, id) => (value) => {
    dispatch(
      updateFieldCreator({
        name: selectedIdsName,
        value: value
          ? [...state[selectedIdsName], id]
          : state[selectedIdsName].filter((certId) => certId !== id),
      }),
    );
  };

  const submit = () => {
    const body = {
      referenceNumber: state.referenceNumber,
      invoiceDate: state.invoiceDate ? moment(state.invoiceDate).format('YYYY-MM-DD') : null,
      invoicePayedDate: state.invoicePayedDate
        ? moment(state.invoicePayedDate).format('YYYY-MM-DD')
        : null,
      vatId: state.vatId,
      memberAssociation: state.memberAssociation,
      dateFrom: state.dateFrom ? moment(state.dateFrom).format('YYYY-MM-DD') : null,
      dateTo: state.dateTo ? moment(state.dateTo).format('YYYY-MM-DD') : null,
    };
    updateInvoice(state.id, body, history);
  };

  const handleAttachCertificates = (id, selectedIds) => {
    attachCertificates(id, selectedIds);
    dispatch(updateFieldCreator({ name: 'resetSelectedIds', value: [] }));
  };

  const handleDetachCertificates = (id, selectedIds) => {
    detachCertificates(id, selectedIds);
    dispatch(updateFieldCreator({ name: 'resetSelectedIds', value: [] }));
  };

  const handleInvoicedFilterChange = (key, value) => {
    setFilters('invoicedCertificates', { name: key, value });
    setPage('invoicedCertificates', 1);
    if (invoice.id) loadInvoicedCertificates(invoice.id);
  };

  const handleInvoicedSortChange = (key, direction) => {
    if (direction) {
      setSort('invoicedCertificates', `${key}.${direction}`);
      if (invoice.id) loadInvoicedCertificates(invoice.id);
    }
  };
  const handleUninvoicedFilterChange = (key, value) => {
    setFilters('uninvoicedCertificates', { name: key, value });
    setPage('uninvoicedCertificates', 1);
    if (invoice.id) loadUninvoicedCertificates(invoice.id);
  };

  const handleUninvoicedSortChange = (key, direction) => {
    if (direction) {
      setSort('uninvoicedCertificates', `${key}.${direction}`);
      if (invoice.id) loadUninvoicedCertificates(invoice.id);
    }
  };

  const handleInvoicedPageChange = (page) => {
    setPage('invoicedCertificates', page);
    if (invoice.id) loadInvoicedCertificates(invoice.id);
  };

  const handleInvoicedPerPageChange = ({ value }) => {
    setPerPage('invoicedCertificates', value);
    setPage('invoicedCertificates', 1);
    if (invoice.id) loadInvoicedCertificates(invoice.id);
  };

  const handleUninvoicedPageChange = (page) => {
    setPage('uninvoicedCertificates', page);
    if (invoice.id) loadUninvoicedCertificates(invoice.id);
  };

  const handleUninvoicedPerPageChange = ({ value }) => {
    setPerPage('uninvoicedCertificates', value);
    setPage('uninvoicedCertificates', 1);
    if (invoice.id) loadUninvoicedCertificates(invoice.id);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.invoices.editInvoice')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="referenceNumber"
                  label={t('page.user.cis.invoices.referenceNumber')}
                  required
                  error={errors.referenceNumber}
                >
                  <input
                    id="referenceNumber"
                    type="text"
                    placeholder={t('page.user.cis.invoices.referenceNumberPlaceholder')}
                    value={state.referenceNumber}
                    onChange={handleChangeNative('referenceNumber', 'referenceNumber')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="invoiceDate"
                  label={t('page.user.cis.invoices.invoiceDate')}
                  value={state.invoiceDate}
                  clear={() => handleChangeCustom('invoiceDate')(null)}
                  required
                  error={errors.invoiceDate}
                >
                  <Datepicker
                    id="invoiceDate"
                    type="text"
                    placeholderText={t('page.user.cis.invoices.invoiceDatePlaceholder')}
                    selected={state.invoiceDate}
                    onChange={handleChangeCustom('invoiceDate', 'invoiceDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="invoicePayedDate"
                  label={t('page.user.cis.invoices.invoicePayedDate')}
                  value={state.invoicePayedDate}
                  clear={() => handleChangeCustom('invoicePayedDate')(null)}
                  error={errors.invoicePayedDate}
                >
                  <Datepicker
                    id="invoicePayedDate"
                    type="text"
                    placeholderText={t('page.user.cis.invoices.invoicePayedDatePlaceholder')}
                    selected={state.invoicePayedDate}
                    onChange={handleChangeCustom('invoicePayedDate', 'invoicePayedDate')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field id="vatId" label={t('page.user.cis.invoices.vatId')} error={errors.vatId}>
                  <input
                    id="vatId"
                    type="text"
                    placeholder={t('page.user.cis.invoices.vatIdPlaceholder')}
                    value={state.vatId}
                    onChange={handleChangeNative('vatId', 'vatId')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="memberAssociation"
                  label={t('page.user.cis.invoices.memberAssociation')}
                  value={ma.find((option) => option.value === state.memberAssociation) || ''}
                  clear={() => handleChangeSelect('memberAssociation')(null)}
                  required
                  error={errors.memberAssociation}
                >
                  <Select
                    id="memberAssociation"
                    type="text"
                    placeholder={t('page.user.cis.invoices.memberAssociation')}
                    value={ma.find((option) => option.value === state.memberAssociation) || ''}
                    options={ma}
                    onChange={handleChangeSelect('memberAssociation', 'memberAssociation')}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateFrom"
                  label={t('page.user.cis.invoices.dateFrom')}
                  value={state.dateFrom}
                  clear={() => handleChangeCustom('dateFrom')(null)}
                  error={errors.dateFrom}
                >
                  <Datepicker
                    id="dateFrom"
                    type="text"
                    placeholderText={t('page.user.cis.invoices.dateFromPlaceholder')}
                    selected={state.dateFrom}
                    onChange={handleChangeCustom('dateFrom', 'dateFrom')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateTo"
                  label={t('page.user.cis.invoices.dateTo')}
                  value={state.dateTo}
                  clear={() => handleChangeCustom('dateTo')(null)}
                  error={errors.dateTo}
                >
                  <Datepicker
                    id="dateTo"
                    type="text"
                    placeholderText={t('page.user.cis.invoices.dateToPlaceholder')}
                    selected={state.dateTo}
                    onChange={handleChangeCustom('dateTo', 'dateTo')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Margin bottom="24">
            <Heading size="h6">{t('page.user.cis.invoices.clientMemberAssociations')}</Heading>
          </Margin>
          <Row>
            <Margin bottom="24">
              <Col size={{ xl: 1 }}>
                <ul style={{ listStyle: 'none', padding: 0 }}>
                  {clientMemberAssociations.length === 0 && (
                    <li style={{ color: 'grey', fontStyle: 'italic' }}>
                      {t('page.user.cis.invoices.noClientMemberAssociations')}
                    </li>
                  )}
                  {clientMemberAssociations.map((item) => (
                    <li key={item.value} style={{ marginBottom: '10px' }}>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </Col>
            </Margin>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={() => submit()}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
      <CisContainer>
        <Margin top="20">
          <Row>
            <Col size={{ xl: 1 / 2 }}>
              <Margin bottom="20">
                <Row style={{ alignItems: 'center' }}>
                  <Col size={{ xl: 1 / 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <Heading size="h5">{t('page.user.cis.invoices.attachedCertificates')}</Heading>
                  </Col>
                  <Col size={{ xl: 1 / 3 }} />
                  <Col size={{ xl: 1 / 3 }}>
                    <Button
                      disabled={
                        !state.invoicedCertificatesSelectedIds?.length ||
                        state.invoicePayedDate != null
                      }
                      onClick={() =>
                        handleDetachCertificates(state.id, state.invoicedCertificatesSelectedIds)
                      }
                    >
                      {t('page.user.cis.invoices.detachCertificates')}
                    </Button>
                  </Col>
                </Row>
              </Margin>
              <div
                style={{
                  overflowX: 'auto',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  border: '2px solid #949494ff',
                  borderRadius: '5px',
                }}
              >
                <CisTable tablebreakpoint="lg">
                  <CisTable.Head>
                    <CisTable.Row>
                      <CisTable.Heading height="5">
                        <TableHeaderCell label={t('page.user.cis.certificates.select')} isAction />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="input"
                          filterValue={invoicedCertificates.filters?.certId || ''}
                          onFilter={(val) => handleInvoicedFilterChange('certId', val)}
                          filterPlaceholder={t(
                            'page.user.cis.certificates.certificateNumberPlaceholder',
                          )}
                          label={t('page.user.cis.certificates.certificateNumber')}
                          sort
                          sortDirection={
                            invoicedCertificates.sort?.split('.')[0] === 'certId'
                              ? invoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleInvoicedSortChange('certId', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="datepicker-range"
                          filterValueFrom={invoicedCertificates.filters?.issueDateFrom || ''}
                          filterValueTo={invoicedCertificates.filters?.issueDateTo || ''}
                          onFilterFrom={(val) => handleInvoicedFilterChange('issueDateFrom', val)}
                          onFilterTo={(val) => handleInvoicedFilterChange('issueDateTo', val)}
                          filterPlaceholder={t('page.user.cis.certificates.issueDatePlaceholder')}
                          label={t('page.user.cis.certificates.issueDate')}
                          sort
                          sortDirection={
                            invoicedCertificates.sort?.split('.')[0] === 'issueDate'
                              ? invoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleInvoicedSortChange('issueDate', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="select"
                          filterValue={invoicedCertificates.filters?.type || ''}
                          onFilter={(val) => handleInvoicedFilterChange('type', val)}
                          filterOptions={typeOptions}
                          filterPlaceholder={t('page.user.cis.certificates.typePlaceholder')}
                          label={t('page.user.cis.certificates.type')}
                          sort
                          sortDirection={
                            invoicedCertificates.sort?.split('.')[0] === 'type'
                              ? invoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleInvoicedSortChange('type', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="datepicker-range"
                          filterValueFrom={invoicedCertificates.filters?.validUntilFrom || ''}
                          filterValueTo={invoicedCertificates.filters?.validUntilTo || ''}
                          onFilterFrom={(val) => handleInvoicedFilterChange('validUntilFrom', val)}
                          onFilterTo={(val) => handleInvoicedFilterChange('validUntilTo', val)}
                          filterPlaceholder={t('page.user.cis.certificates.validUntilPlaceholder')}
                          label={t('page.user.cis.certificates.validUntil')}
                          sort
                          sortDirection={
                            invoicedCertificates.sort?.split('.')[0] === 'validUntil'
                              ? invoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleInvoicedSortChange('validUntil', dir)}
                        />
                      </CisTable.Heading>
                    </CisTable.Row>
                  </CisTable.Head>
                  <CisTable.Body>
                    {certificatesLoading ? (
                      <CisTable.Row>
                        <CisTable.Cell colSpan={6}>
                          <Spinner />
                        </CisTable.Cell>
                      </CisTable.Row>
                    ) : null}
                    {!certificatesLoading && !invoicedCertificates.items?.length ? (
                      <CisTable.Row>
                        <CisTable.Cell colSpan={6}>{t('global.noResultsFound')}</CisTable.Cell>
                      </CisTable.Row>
                    ) : null}
                    {!certificatesLoading && invoicedCertificates.items?.length
                      ? invoicedCertificates.items.map((item) => (
                          <CisTable.Row key={item.id}>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.select')}:
                              </CisTable.Label>
                              <Checkbox
                                selected={state.invoicedCertificatesSelectedIds?.includes(item.id)}
                                onChange={handleSelectRow(
                                  'invoicedCertificatesSelectedIds',
                                  item.id,
                                )}
                              />
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.certificateNumber')}:
                              </CisTable.Label>
                              {item.certId}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.issueDate')}:
                              </CisTable.Label>
                              {item.issueDate && moment(item.issueDate).format('DD/MM/YYYY')}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.type')}:
                              </CisTable.Label>
                              {item.type}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.validUntil')}:
                              </CisTable.Label>
                              {item.validUntil && moment(item.validUntil).format('DD/MM/YYYY')}
                            </CisTable.Cell>
                          </CisTable.Row>
                        ))
                      : null}
                  </CisTable.Body>
                </CisTable>
              </div>
              <CisPagination
                styled
                currentPage={invoicedCertificates.page || 1}
                totalPages={invoicedCertificates.pages || 1}
                totalItems={invoicedCertificates.totalItems || 0}
                perPage={invoicedCertificates.perPage || 10}
                onPageChange={handleInvoicedPageChange}
                onPerPageChange={handleInvoicedPerPageChange}
              />
            </Col>
            <Col size={{ xl: 1 / 2 }}>
              <Margin bottom="20">
                <Row style={{ alignItems: 'center' }}>
                  <Col size={{ xl: 1 / 3 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <Heading size="h5">{t('page.user.cis.invoices.detachedCertificates')}</Heading>
                  </Col>
                  <Col size={{ xl: 1 / 3 }} />
                  <Col size={{ xl: 1 / 3 }}>
                    <Button
                      disabled={
                        !state.uninvoicedCertificatesSelectedIds?.length ||
                        state.invoicePayedDate != null
                      }
                      onClick={() =>
                        handleAttachCertificates(state.id, state.uninvoicedCertificatesSelectedIds)
                      }
                    >
                      {t('page.user.cis.invoices.attachCertificates')}
                    </Button>
                  </Col>
                </Row>
              </Margin>
              <div
                style={{
                  overflowX: 'auto',
                  maxHeight: '70vh',
                  overflowY: 'auto',
                  border: '2px solid #949494ff',
                  borderRadius: '5px',
                }}
              >
                <CisTable tablebreakpoint="lg">
                  <CisTable.Head>
                    <CisTable.Row>
                      <CisTable.Heading height="5">
                        <TableHeaderCell label={t('page.user.cis.certificates.select')} isAction />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="input"
                          filterValue={uninvoicedCertificates.filters?.certId || ''}
                          onFilter={(val) => handleUninvoicedFilterChange('certId', val)}
                          filterPlaceholder={t(
                            'page.user.cis.certificates.certificateNumberPlaceholder',
                          )}
                          label={t('page.user.cis.certificates.certificateNumber')}
                          sort
                          sortDirection={
                            uninvoicedCertificates.sort?.split('.')[0] === 'certId'
                              ? uninvoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleUninvoicedSortChange('certId', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="datepicker-range"
                          filterValueFrom={uninvoicedCertificates.filters?.issueDateFrom || ''}
                          filterValueTo={uninvoicedCertificates.filters?.issueDateTo || ''}
                          onFilterFrom={(val) => handleUninvoicedFilterChange('issueDateFrom', val)}
                          onFilterTo={(val) => handleUninvoicedFilterChange('issueDateTo', val)}
                          filterPlaceholder={t('page.user.cis.certificates.issueDatePlaceholder')}
                          label={t('page.user.cis.certificates.issueDate')}
                          sort
                          sortDirection={
                            uninvoicedCertificates.sort?.split('.')[0] === 'issueDate'
                              ? uninvoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleUninvoicedSortChange('issueDate', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="select"
                          filterValue={uninvoicedCertificates.filters?.type || ''}
                          onFilter={(val) => handleUninvoicedFilterChange('type', val)}
                          filterOptions={typeOptions}
                          filterPlaceholder={t('page.user.cis.certificates.typePlaceholder')}
                          label={t('page.user.cis.certificates.type')}
                          sort
                          sortDirection={
                            uninvoicedCertificates.sort?.split('.')[0] === 'type'
                              ? uninvoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleUninvoicedSortChange('type', dir)}
                        />
                      </CisTable.Heading>
                      <CisTable.Heading height="5">
                        <TableHeaderCell
                          filter
                          filterType="datepicker-range"
                          filterValueFrom={uninvoicedCertificates.filters?.validUntilFrom || ''}
                          filterValueTo={uninvoicedCertificates.filters?.validUntilTo || ''}
                          onFilterFrom={(val) =>
                            handleUninvoicedFilterChange('validUntilFrom', val)
                          }
                          onFilterTo={(val) => handleUninvoicedFilterChange('validUntilTo', val)}
                          filterPlaceholder={t('page.user.cis.certificates.validUntilPlaceholder')}
                          label={t('page.user.cis.certificates.validUntil')}
                          sort
                          sortDirection={
                            uninvoicedCertificates.sort?.split('.')[0] === 'validUntil'
                              ? uninvoicedCertificates.sort?.split('.')[1]
                              : null
                          }
                          onSort={(dir) => handleUninvoicedSortChange('validUntil', dir)}
                        />
                      </CisTable.Heading>
                    </CisTable.Row>
                  </CisTable.Head>
                  <CisTable.Body>
                    {certificatesLoading ? (
                      <CisTable.Row>
                        <CisTable.Cell colSpan={6}>
                          <Spinner />
                        </CisTable.Cell>
                      </CisTable.Row>
                    ) : null}
                    {!certificatesLoading && !uninvoicedCertificates.items?.length ? (
                      <CisTable.Row>
                        <CisTable.Cell colSpan={6}>{t('global.noResultsFound')}</CisTable.Cell>
                      </CisTable.Row>
                    ) : null}
                    {!certificatesLoading && uninvoicedCertificates.items?.length
                      ? uninvoicedCertificates.items.map((item) => (
                          <CisTable.Row key={item.id}>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.select')}:
                              </CisTable.Label>
                              <Checkbox
                                selected={state.uninvoicedCertificatesSelectedIds?.includes(
                                  item.id,
                                )}
                                onChange={handleSelectRow(
                                  'uninvoicedCertificatesSelectedIds',
                                  item.id,
                                )}
                              />
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.certificateNumber')}:
                              </CisTable.Label>
                              {item.certId}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.issueDate')}:
                              </CisTable.Label>
                              {item.issueDate && moment(item.issueDate).format('DD/MM/YYYY')}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.type')}:
                              </CisTable.Label>
                              {item.type}
                            </CisTable.Cell>
                            <CisTable.Cell>
                              <CisTable.Label>
                                {t('page.user.cis.certificates.validUntil')}:
                              </CisTable.Label>
                              {item.validUntil && moment(item.validUntil).format('DD/MM/YYYY')}
                            </CisTable.Cell>
                          </CisTable.Row>
                        ))
                      : null}
                  </CisTable.Body>
                </CisTable>
              </div>
              <CisPagination
                styled
                currentPage={uninvoicedCertificates.page || 1}
                totalPages={uninvoicedCertificates.pages || 1}
                totalItems={uninvoicedCertificates.totalItems || 0}
                perPage={uninvoicedCertificates.perPage || 10}
                onPageChange={handleUninvoicedPageChange}
                onPerPageChange={handleUninvoicedPerPageChange}
              />
            </Col>
          </Row>
        </Margin>
      </CisContainer>
    </PageContent>
  );
};

EditInvoice.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.number,
    referenceNumber: PropTypes.number,
    invoiceDate: PropTypes.any,
    invoicePayedDate: PropTypes.any,
    vatId: PropTypes.number,
    memberAssociation: PropTypes.number,
    clientmemberAssociation: PropTypes.number,
    dateFrom: PropTypes.any,
    dateTo: PropTypes.any,
  }),
  errors: PropTypes.shape({
    referenceNumber: PropTypes.string,
    invoiceDate: PropTypes.string,
    invoicePayedDate: PropTypes.string,
    vatId: PropTypes.string,
    memberAssociation: PropTypes.string,
    clientmemberAssociation: PropTypes.string,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
  }),
  createInvoice: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  loadCertificates: PropTypes.func.isRequired,
  removeCertificates: PropTypes.func.isRequired,
  attachCertificates: PropTypes.func.isRequired,
  detachCertificates: PropTypes.func.isRequired,
  ma: PropTypes.arrayOf(globalPropTypes.ChooseDataPropsTypes).isRequired,
};

EditInvoice.defaultProps = {
  invoice: {},
  errors: {},
};

const mapStateToProps = ({ loading, ipma, globals }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  certificatesLoading: isLoading(loading, 'LOAD_IPMA_CIS_INVOICE_CERTIFICATES'),
  invoicedCertificates: ipma.cis.invoicedCertificates,
  uninvoicedCertificates: ipma.cis.uninvoicedCertificates,
  invoice: ipma.cis.invoices.profile,
  errors: ipma.cis.invoices.editErrors,
  ma: globals.availableMA,
  clientMemberAssociations: globals.cis.clientMemberAssociations,
});

const mapDispatchToProps = (dispatch) => ({
  updateInvoice: (id, payload, history) =>
    dispatch(updateIpmaCisCreator(id, 'invoices', payload, history)),
  removeError: (payload) => dispatch(setIpmaCisEditErrorsCreator('invoices', payload)),
  loadCertificates: (payload) => dispatch(loadIpmaCisInvoiceCertificatesCreator(payload)),
  loadInvoicedCertificates: (payload) =>
    dispatch(loadIpmaCisInvoiceInvoicedCertificatesCreator(payload)),
  loadUninvoicedCertificates: (payload) =>
    dispatch(loadIpmaCisInvoiceUninvoicedCertificatesCreator(payload)),
  removeCertificates: () => dispatch(removeIpmaCisInvoiceCertificatesCreator()),
  attachCertificates: (id, payload) =>
    dispatch(attachIpmaCisInvoiceCertificatesCreator(id, payload)),
  detachCertificates: (id, payload) =>
    dispatch(detachIpmaCisInvoiceCertificatesCreator(id, payload)),
  loadInvoicesAttributes: () => dispatch(loadGlobalCisInvoicesAttributesCreator()),
  loadClientMemberAssociations: (payload) =>
    dispatch(loadClientMemberAssociationsByMACreator(payload)),
  setPage: (cisType, payload) => dispatch(setIpmaCisPageCreator(cisType, payload)),
  setPerPage: (cisType, payload) => dispatch(setIpmaCisPerPageCreator(cisType, payload)),
  setSort: (cisType, payload) => dispatch(setIpmaCisSortCreator(cisType, payload)),
  setFilters: (cisType, payload) => dispatch(setIpmaCisFiltersCreator(cisType, payload)),
  setClientMemberAssociations: (payload) => dispatch(setClientMemberAssociationsCreator(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditInvoice);
